import CloseIcon from "@mui/icons-material/Close";
import EmailIcon from "@mui/icons-material/Email";
import FacebookIcon from "@mui/icons-material/Facebook";
import FilterListIcon from "@mui/icons-material/FilterList";
import InsightsIcon from "@mui/icons-material/Insights";
import InstagramIcon from "@mui/icons-material/Instagram";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import PhoneIcon from "@mui/icons-material/Phone";
import SearchIcon from "@mui/icons-material/Search";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import TwitterIcon from "@mui/icons-material/Twitter";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { Box } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import moment from "moment";
import React, { useCallback, useEffect, useState } from "react";
import {
  fetchCityList,
  fetchEventlist,
  fetchPincodeList,
  fetchStateList,
} from "../../util/axiosConfig/api/api";
import { decryptWithSpaces } from "../../util/DecryptWithSpaces";
import DspHeader from "./DspHeader";
import EventCardLarge from "./EventCardLarge";
import EventCardSmall from "./EventCardSmall";
import "./EventsLanding.css";
import SkeletonLoader from "./SkeletonLoader";
import SkeletonSmall from "./SkeletonSmall";

const EventsLanding = () => {
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [pastEvents, setPastEvents] = useState([]);
  const [todaysEvents, setTodaysEvents] = useState([]);
  const [showAllUpcoming, setShowAllUpcoming] = useState(false);
  const [showAllPast, setShowAllPast] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Filter parameters (writable so FilterModal can update them)
  const [filters, setFilters] = useState({
    state: "NA",
    city: "NA",
    pincode: "NA",
    date: "NA",
    subject: "NA",
  });

  // Temporary filter state for the dropdown
  const [tempFilters, setTempFilters] = useState({
    month: null,
    state: "",
    city: "",
    pincode: "",
  });

  // Dynamic options for dropdowns
  const [stateOptions, setStateOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [pinOptions, setPinOptions] = useState([]);

  // Date range for DatePicker (current year -1 to +3 years)
  const minDateRange = dayjs().subtract(1, "years");
  const maxDateRange = dayjs().add(3, "years");

  const handleApplyFilter = () => {
    // Convert temp filters to API format and trigger re-fetch
    let formattedDate = "NA";
    if (tempFilters.month) {
      try {
        // Handle dayjs object - format as MM-YYYY for month filtering
        if (tempFilters.month.$d) {
          formattedDate = moment(tempFilters.month.$d).format("MM-YYYY");
        } else if (tempFilters.month.toDate) {
          formattedDate = moment(tempFilters.month.toDate()).format("MM-YYYY");
        } else {
          formattedDate = moment(tempFilters.month).format("MM-YYYY");
        }
      } catch (error) {
        console.error("Error formatting date:", error);
      }
    }

    setFilters((prev) => ({
      ...prev,
      state: tempFilters.state || "NA",
      city: tempFilters.city || "NA",
      pincode: tempFilters.pincode || "NA",
      date: formattedDate,
    }));
    setShowFilterDropdown(false);
  };

  const handleClearFilter = () => {
    setTempFilters({
      month: null,
      state: "",
      city: "",
      pincode: "",
    });
    setFilters({
      state: "NA",
      city: "NA",
      pincode: "NA",
      date: "NA",
      subject: "NA",
    });
    setCityOptions([]);
    setPinOptions([]);
    setShowFilterDropdown(false);
  };

  const isFilterApplied =
    filters.state !== "NA" ||
    filters.city !== "NA" ||
    filters.pincode !== "NA" ||
    (filters.date !== "NA" && filters.date !== "");

  // Fetch state list on component mount
  useEffect(() => {
    handlefetchStateList();
  }, []);

  const handlefetchStateList = async () => {
    try {
      const res = await fetchStateList();
      if (res) {
        const options = res.map((item) => ({
          value: item,
          label: item,
        }));
        setStateOptions(options);
      }
    } catch (error) {
      console.error("Error fetching state list:", error);
    }
  };

  const handlefetchCityList = async (state) => {
    setCityOptions([]);
    setPinOptions([]);
    try {
      const res = await fetchCityList(state);
      if (res) {
        const options = res.map((item) => ({
          value: item,
          label: item,
        }));
        setCityOptions(options);
      }
    } catch (error) {
      console.error("Error fetching city list:", error);
    }
  };

  const handlefetchPincodeList = async (city) => {
    setPinOptions([]);
    try {
      const res = await fetchPincodeList(city);
      if (res) {
        const options = res.map((item) => ({
          value: item,
          label: item,
        }));
        setPinOptions(options);
      }
    } catch (error) {
      console.error("Error fetching pincode list:", error);
    }
  };

  const handleSearch = useCallback(() => {
    // Use `subject` as the search parameter for fetchEventlist
    setFilters((prev) => ({ ...prev, subject: searchQuery || "NA" }));
  }, [searchQuery]);

  // Auto-search with debounce when searchQuery changes
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      handleSearch();
    }, 500); // 500ms delay after user stops typing

    return () => clearTimeout(debounceTimer);
  }, [searchQuery, handleSearch]);

  // Transform API data to match EventCard component expectations
  const transformEventData = async (events) => {
    const transformedEvents = await Promise.all(
      events.map(async (event) => {
        const decryptedTrainer = event.trainerName
          ? await decryptWithSpaces(event.trainerName)
          : "";

        // Determine event type. If API provides `offline` boolean, interpret
        // per user request: offline === true => "offline", offline === false => "online"
        let eventType = "online";
        if (typeof event.offline !== "undefined") {
          const offVal = event.offline;
          const offIsOffline =
            offVal === true ||
            String(offVal).toLowerCase() === "true" ||
            Number(offVal) === 1 ||
            (typeof offVal === "string" &&
              String(offVal).toLowerCase().includes("offline"));
          eventType = offIsOffline ? "offline" : "online";
        } else if (
          event.venue &&
          event.venue.toLowerCase().includes("online")
        ) {
          eventType = "online";
        } else if (event.type) {
          eventType = event.type;
        } else if (event.eventType) {
          eventType = event.eventType;
        } else if (
          event.place ||
          event.address ||
          (event.venue && event.venue.toLowerCase() !== "online")
        ) {
          eventType = "offline";
        }

        // Normalize to canonical values: "online" or "offline"
        eventType = String(eventType || "").toLowerCase();
        if (eventType.includes("online")) eventType = "online";
        else if (eventType.includes("offline")) eventType = "offline";
        else eventType = "online"; // default

        return {
          ...event,
          title: event.eventTopicName || event.title || "",
          trainer: decryptedTrainer || event.trainer || "",
          date: event.startTime
            ? moment(event.startTime).format("DD MMM, YYYY")
            : event.date || "",
          time: event.startTime
            ? moment(event.startTime).format("hh:mm A")
            : event.time || "",
          type: eventType,
        };
      }),
    );
    return transformedEvents;
  };

  // Compute filtered arrays based on event dates
  // Treat events as past when startTime < now - t1 (t1 = 1 day)
  const computeFilteredArrays = (storeData) => {
    const nowMs = Date.now();
    const oneDayMs = 24 * 60 * 60 * 1000; // 1 day
    const pastThresholdMs = nowMs - oneDayMs; // events older than this are past

    const todayStartMs = new Date().setHours(0, 0, 0, 0);
    const momenttoday = moment(new Date()).format("DD-MM-YYYY");

    const safeTime = (ev) => {
      const t = new Date(ev.startTime).getTime();
      return isNaN(t) ? 0 : t;
    };

    // Past: strictly older than 1 day
    const filteredpastData = storeData.filter((event) => {
      return safeTime(event) > 0 && safeTime(event) < pastThresholdMs;
    });

    // Today: same calendar day as today and not older than the 1-day threshold
    const filteredtodaysData = storeData.filter((event) => {
      const evDateStr = moment(new Date(event.startTime)).format("DD-MM-YYYY");
      return evDateStr === momenttoday && safeTime(event) >= pastThresholdMs;
    });

    const filteredTomorrowData = [];

    // Upcoming: events with date > today (future calendar days)
    const filteredUpcomingData = storeData.filter((event) => {
      const evDateMs = new Date(event.startTime).setHours(0, 0, 0, 0);
      return evDateMs > todayStartMs;
    });

    // Sort results: past = most recent first, today/upcoming = soonest first
    filteredpastData.sort((a, b) => safeTime(b) - safeTime(a));
    filteredtodaysData.sort((a, b) => safeTime(a) - safeTime(b));
    filteredUpcomingData.sort((a, b) => safeTime(a) - safeTime(b));

    return {
      filteredpastData,
      filteredtodaysData,
      filteredTomorrowData,
      filteredUpcomingData,
    };
  };

  const loadEvents = useCallback(async () => {
    // Create abort controller for this request
    const abortController = new AbortController();

    try {
      setLoading(true);
      setError(null);

      console.log("Fetching events with filters:", filters);

      const response = await fetchEventlist(
        filters.state,
        filters.city,
        filters.pincode,
        filters.date,
        filters.subject,
      );

      // Check if request was aborted
      if (abortController.signal.aborted) {
        console.log("Request aborted");
        return;
      }

      console.log("API Response:", response);

      if (response && Array.isArray(response) && response.length > 0) {
        console.log("Response data array length:", response.length);

        // Transform API data first
        const transformedData = await transformEventData(response);

        // Check again after async transformation
        if (abortController.signal.aborted) {
          console.log("Request aborted during transformation");
          return;
        }

        // Use computeFilteredArrays to categorize events
        const { filteredpastData, filteredtodaysData, filteredUpcomingData } =
          computeFilteredArrays(transformedData);

        // Log event types for debugging
        const eventTypes = transformedData.map((e) => ({
          id: e.id,
          type: e.type,
          title: e.title,
        }));
        console.log("Event types:", eventTypes);

        setPastEvents(filteredpastData);
        setTodaysEvents(filteredtodaysData);
        setUpcomingEvents(filteredUpcomingData);
      } else {
        console.warn("No data in response:", response);
        // Don't set error for empty results
        setPastEvents([]);
        setTodaysEvents([]);
        setUpcomingEvents([]);
      }
    } catch (err) {
      // Don't show error if request was aborted
      if (abortController.signal.aborted) {
        console.log("Request aborted in error handler");
        return;
      }

      console.error("Error fetching events:", err);
      console.error("Error details:", {
        message: err.message,
        response: err.response,
        stack: err.stack,
      });
      setError(
        `Failed to load events: ${err.message || "Please try again later."}`,
      );
    } finally {
      if (!abortController.signal.aborted) {
        setLoading(false);
      }
    }

    // Return cleanup function
    return () => {
      abortController.abort();
    };
  }, [filters]);

  useEffect(() => {
    const cleanup = loadEvents();

    // Cleanup on unmount or when loadEvents changes
    return () => {
      if (cleanup && typeof cleanup.then === "function") {
        cleanup.then((cleanupFn) => {
          if (cleanupFn) cleanupFn();
        });
      }
    };
  }, [loadEvents]);

  // Clean up state when component unmounts to ensure fresh data on return
  useEffect(() => {
    return () => {
      // Clear events data on unmount
      setUpcomingEvents([]);
      setPastEvents([]);
      setTodaysEvents([]);
    };
  }, []);

  // Auto-dismiss error after 1 second
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [error]);

  // Close filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showFilterDropdown &&
        !event.target.closest(".filter-dropdown-wrapper") &&
        !event.target.closest(".MuiPickersPopper-root") &&
        !event.target.closest(".MuiDialog-root")
      ) {
        setShowFilterDropdown(false);
      }
    };

    if (showFilterDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showFilterDropdown]);

  const getFilteredEvents = (events) => {
    if (!events || events.length === 0) return [];

    console.log(`Filtering ${events.length} events for tab: ${activeTab}`);

    if (activeTab === "all") return events;

    const filtered = events.filter((event) => {
      const eventType = (event.type || "").toLowerCase();
      const matches = eventType === activeTab.toLowerCase();
      console.log(
        `Event "${event.title}" type: "${event.type}" matches "${activeTab}": ${matches}`,
      );
      return matches;
    });

    console.log(`Filtered result: ${filtered.length} events`);
    return filtered;
  };

  // Combine today's events with upcoming events for display
  const allUpcomingEvents = [...todaysEvents, ...upcomingEvents];

  console.log(`Active Tab: ${activeTab}`);
  console.log(
    `Today's events: ${todaysEvents.length}, Upcoming: ${upcomingEvents.length}, Combined: ${allUpcomingEvents.length}`,
  );
  console.log(`Past events: ${pastEvents.length}`);

  const displayUpcomingEvents = getFilteredEvents(allUpcomingEvents);
  const displayPastEvents = getFilteredEvents(pastEvents);

  // Limits for landing page previews
  const UPCOMING_LIMIT = 3;
  const PAST_LIMIT = 4;

  const shownUpcomingEvents = showAllUpcoming
    ? displayUpcomingEvents
    : displayUpcomingEvents.slice(0, UPCOMING_LIMIT);

  const shownPastEvents = showAllPast
    ? displayPastEvents
    : displayPastEvents.slice(0, PAST_LIMIT);

  const hasNoEvents =
    displayUpcomingEvents.length === 0 && displayPastEvents.length === 0;

  return (
    <React.Fragment>
      <div className="events-landing">
        {/* Hero Section */}
        <section className="events-hero">
          <div className="events-hero-bg">
            <div className="hero-wave"></div>
          </div>

          {/* Custom Header */}
          <DspHeader />

          <div className="container-lg">
            <div className="row">
              <div className="col-lg-7">
                <div className="hero-content">
                  <h1 className="hero-title">
                    <span className="text-highlight">Learn</span> about Mutual
                    Funds
                  </h1>
                  <p className="hero-description">
                    Discover webinars and offline events to learn about mutual
                    funds and make better investment decisions.
                  </p>
                  <div className="hero-tags">
                    <span className="hero-tag">
                      <InsightsIcon className="tag-icon" />
                      Gain valuable insight
                    </span>
                    <span className="hero-tag">
                      <LightbulbIcon className="tag-icon" />
                      Empower yourself financially
                    </span>
                    <span className="hero-tag">
                      <TouchAppIcon className="tag-icon" />
                      Interact and engage
                    </span>
                  </div>
                </div>
              </div>
              <div className="col-lg-5">
                <div className="hero-illustration">
                  {/* Illustration placeholder */}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Events Section */}
        <section className="events-section">
          <div className="events-bg-circles">
            <div className="circle circle-1"></div>
            <div className="circle circle-2"></div>
          </div>

          <div className="container-lg">
            {/* Filter Bar */}
            <div className="events-filter-bar">
              <div className="filter-tabs">
                <button
                  className={`filter-tab ${activeTab === "all" ? "active" : ""}`}
                  onClick={() => setActiveTab("all")}
                >
                  All Events
                </button>
                <button
                  className={`filter-tab ${activeTab === "online" ? "active" : ""}`}
                  onClick={() => setActiveTab("online")}
                >
                  Online
                </button>
                <button
                  className={`filter-tab ${activeTab === "offline" ? "active" : ""}`}
                  onClick={() => setActiveTab("offline")}
                >
                  Offline
                </button>
              </div>
              <div className="filter-actions">
                <div className="search-input-wrap">
                  <SearchIcon className="search-icon-left" />
                  <input
                    type="text"
                    className="search-input"
                    placeholder="Search events by name"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <button
                      className="search-clear-btn"
                      onClick={() => setSearchQuery("")}
                      aria-label="Clear search"
                    >
                      <CloseIcon className="clear-icon" />
                    </button>
                  )}
                </div>
                <div className="filter-dropdown-wrapper">
                  <button
                    className={`filter-btn ${isFilterApplied ? "filter-applied" : ""}`}
                    onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                  >
                    <FilterListIcon className="filter-icon" />
                    {isFilterApplied ? "Filter Applied" : "Filter"}
                  </button>
                  {showFilterDropdown && (
                    <div className="filter-dropdown">
                      <h3 className="filter-dropdown-title">Filter by:</h3>
                      <div className="filter-dropdown-body">
                        <div className="filter-row">
                          <div className="filter-field">
                            <label className="filter-label">Month</label>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <DesktopDatePicker
                                displayStaticWrapperAs="desktop"
                                clearable
                                views={["month", "year"]}
                                inputFormat="MMM-YYYY"
                                openTo="month"
                                minDate={minDateRange}
                                maxDate={maxDateRange}
                                value={tempFilters.month}
                                onChange={(newValue) => {
                                  setTempFilters({
                                    ...tempFilters,
                                    month: newValue,
                                  });
                                }}
                                renderInput={({
                                  inputRef,
                                  inputProps,
                                  InputProps,
                                }) => (
                                  <Box>
                                    <div className="filter-date-wrapper">
                                      <input
                                        {...inputProps}
                                        ref={inputRef}
                                        type="text"
                                        className="filter-input"
                                        placeholder="Select month"
                                        readOnly
                                      />
                                      {InputProps?.endAdornment}
                                    </div>
                                  </Box>
                                )}
                              />
                            </LocalizationProvider>
                          </div>
                          <div className="filter-field">
                            <label className="filter-label">State</label>
                            <select
                              className="filter-input"
                              value={tempFilters.state}
                              onChange={(e) => {
                                const selectedState = e.target.value;
                                setTempFilters({
                                  ...tempFilters,
                                  state: selectedState,
                                  city: "",
                                  pincode: "",
                                });
                                if (selectedState) {
                                  handlefetchCityList(selectedState);
                                } else {
                                  setCityOptions([]);
                                  setPinOptions([]);
                                }
                              }}
                            >
                              <option value="">Choose an option</option>
                              {stateOptions.map((option, index) => (
                                <option key={index} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="filter-row">
                          <div className="filter-field">
                            <label className="filter-label">City</label>
                            <select
                              className="filter-input"
                              value={tempFilters.city}
                              onChange={(e) => {
                                const selectedCity = e.target.value;
                                setTempFilters({
                                  ...tempFilters,
                                  city: selectedCity,
                                  pincode: "",
                                });
                                if (selectedCity) {
                                  handlefetchPincodeList(selectedCity);
                                } else {
                                  setPinOptions([]);
                                }
                              }}
                              disabled={!tempFilters.state}
                            >
                              <option value="">Choose an option</option>
                              {cityOptions.map((option, index) => (
                                <option key={index} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="filter-field">
                            <label className="filter-label">Pincode</label>
                            <select
                              className="filter-input"
                              value={tempFilters.pincode}
                              onChange={(e) => {
                                setTempFilters({
                                  ...tempFilters,
                                  pincode: e.target.value,
                                });
                              }}
                              disabled={!tempFilters.city}
                            >
                              <option value="">Choose an option</option>
                              {pinOptions.map((option, index) => (
                                <option key={index} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                      <div className="filter-dropdown-footer">
                        <button
                          className="clear-filter-btn"
                          onClick={handleClearFilter}
                        >
                          Clear Filter
                        </button>
                        <button
                          className="apply-filter-btn"
                          onClick={handleApplyFilter}
                        >
                          Apply Filter
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}

            {/* No Events Message */}
            {!error && !loading && hasNoEvents && (
              <div className="alert alert-info text-center" role="alert">
                <h4>No Events Found</h4>
                <p>
                  There are currently no {activeTab !== "all" ? activeTab : ""}{" "}
                  events available. Please check back later!
                </p>
              </div>
            )}

            {/* Upcoming Events */}
            {loading ? (
              <SkeletonLoader count={6} />
            ) : (
              <div className="events-list-section">
                <h2 className="section-title">Upcoming Events</h2>

                {shownUpcomingEvents.length > 0 ? (
                  <>
                    <div className="row">
                      {shownUpcomingEvents.map((event) => (
                        <div key={event.id} className="col-lg-4 col-md-6 mb-4">
                          <EventCardLarge event={event} />
                        </div>
                      ))}
                    </div>

                    {displayUpcomingEvents.length > UPCOMING_LIMIT && (
                      <div className="text-center mt-4">
                        <button
                          className="view-all-btn"
                          onClick={() => setShowAllUpcoming(!showAllUpcoming)}
                        >
                          {showAllUpcoming
                            ? "Show less"
                            : "View all upcoming events"}
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="no-events-message">
                    <p>No upcoming events found. Please check back later!</p>
                  </div>
                )}
              </div>
            )}

            {/* Past Events */}
            {loading ? (
              <SkeletonSmall count={4} />
            ) : (
              <div className="events-list-section mt-5">
                <h2 className="section-title">Past Events</h2>

                {shownPastEvents.length > 0 ? (
                  <>
                    <div className="row">
                      {shownPastEvents.map((event) => (
                        <div key={event.id} className="col-lg-3 col-md-6 mb-4">
                          <EventCardSmall event={event} />
                        </div>
                      ))}
                    </div>

                    {displayPastEvents.length > PAST_LIMIT && (
                      <div className="text-center mt-4">
                        <button
                          className="view-all-btn"
                          onClick={() => setShowAllPast(!showAllPast)}
                        >
                          {showAllPast ? "Show less" : "View all past events"}
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="no-events-message">
                    <p>No past events found.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>

        {/* Custom Footer */}
        <footer className="events-footer">
          <div className="footer-wave"></div>
          <div className="container-lg">
            <div className="footer-content">
              {/* Contact Section */}
              <div className="footer-top">
                <div className="footer-contact-section">
                  <h3 className="footer-title">Contact us</h3>
                  <div className="contact-items">
                    <div className="contact-item">
                      <div className="contact-icon">
                        <EmailIcon className="icon" />
                      </div>
                      <div className="contact-details">
                        <p className="contact-label">For any queries</p>
                        <p className="contact-value">service@dspim.com</p>
                      </div>
                    </div>

                    <div className="contact-divider"></div>

                    <div className="contact-item">
                      <div className="contact-icon">
                        <PhoneIcon className="icon" />
                      </div>
                      <div className="contact-details">
                        <p className="contact-label">
                          India : 1800-208-4499 / 1800-200-4499
                        </p>
                        <p className="contact-value">
                          International : 044-61360066
                        </p>
                      </div>
                    </div>

                    <div className="contact-divider"></div>

                    <div className="contact-item">
                      <div className="contact-icon">
                        <WhatsAppIcon className="icon" />
                      </div>
                      <div className="contact-details">
                        <p className="contact-label">7738000030</p>
                        <p className="contact-value">
                          Say 'Hi' to us on Whatsapp
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="footer-right-section">
                  <div className="download-app">
                    <h4 className="download-title">Download our app</h4>
                    <div className="app-buttons">
                      <button
                        className="app-store-btn"
                        onClick={(e) => e.preventDefault()}
                      >
                        <img
                          src="https://upload.wikimedia.org/wikipedia/commons/3/3c/Download_on_the_App_Store_Badge.svg"
                          alt="App Store"
                        />
                      </button>
                      <button
                        className="play-store-btn"
                        onClick={(e) => e.preventDefault()}
                      >
                        <img
                          src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                          alt="Google Play"
                        />
                      </button>
                    </div>
                  </div>

                  <div className="social-media">
                    <h4 className="social-title">Follow us</h4>
                    <div className="social-icons">
                      <button
                        className="social-icon twitter"
                        aria-label="Twitter"
                        onClick={(e) => e.preventDefault()}
                      >
                        <TwitterIcon />
                      </button>
                      <button
                        className="social-icon instagram"
                        aria-label="Instagram"
                        onClick={(e) => e.preventDefault()}
                      >
                        <InstagramIcon />
                      </button>
                      <button
                        className="social-icon facebook"
                        aria-label="Facebook"
                        onClick={(e) => e.preventDefault()}
                      >
                        <FacebookIcon />
                      </button>
                      <button
                        className="social-icon linkedin"
                        aria-label="LinkedIn"
                        onClick={(e) => e.preventDefault()}
                      >
                        <LinkedInIcon />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="footer-divider"></div>

              <div className="footer-bottom">
                <p className="disclaimer">
                  Mutual fund investments are subject to market risks, read all
                  scheme related documents carefully.
                </p>
                <button
                  className="privacy-link"
                  onClick={(e) => e.preventDefault()}
                >
                  Privacy Policy
                </button>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </React.Fragment>
  );
};

export default EventsLanding;
